import {
  COLUMN_WIDTH_SEMANTICS,
  TABLE_ALIGNMENT,
  TABLE_CELL_OVERFLOW,
  TABLE_CELL_TEXT_OVERFLOW,
  TABLE_CELL_WHITE_SPACE,
  TABLE_DENSITY,
  TABLE_DIVIDER_STYLE,
  TABLE_OVERFLOW,
  TABLE_TOOLTIP_MODE,
  TABLE_TOOLTIP_STATE,
} from './table-constants'

/**
 * Extrai os valores de um objeto como união de tipos.
 *
 * @template T Objeto base.
 */
type ValueOf<T> = T[keyof T]

/**
 * Representa um valor numérico com unidade fixa em pixels.
 *
 * @example 100px
 */
type CSSFixed = `${number}px`

/**
 * Representa um valor numérico com unidade fr (fração de grid).
 *
 * @example 1fr
 */
type CSSFraction = `${number}fr`

/**
 * Define as larguras de coluna suportadas pela tabela.
 *
 * @remarks Valores possíveis:
 * - fill
 * - content
 * - ${number}px
 * - ${number}fr
 */
export type ColumnWidth = ValueOf<typeof COLUMN_WIDTH_SEMANTICS> | CSSFixed | CSSFraction

/**
 * Define as densidades suportadas pela tabela.
 *
 * @remarks Valores possíveis:
 * - small
 * - medium
 * - large
 */
export type TableDensity = ValueOf<typeof TABLE_DENSITY>

/**
 * Define os alinhamentos suportados para o conteúdo das células.
 *
 * @remarks Valores possíveis:
 * - start
 * - center
 * - end
 */
export type TableAlignment = ValueOf<typeof TABLE_ALIGNMENT>

/**
 * Define os estilos de divisor suportados.
 *
 * @remarks Valores possíveis:
 * - solid
 * - dashed
 */
export type TableDividerStyle = ValueOf<typeof TABLE_DIVIDER_STYLE>

/**
 * Define as opções de overflow para células da tabela.
 *
 * @remarks Valores possíveis:
 * - hidden
 * - visible
 */
export type TableCellOverflow = ValueOf<typeof TABLE_CELL_OVERFLOW>

/**
 * Define as opções de overflow de texto para células da tabela.
 *
 * @remarks Valores possíveis:
 * - ellipsis
 * - clip
 */
export type TableCellTextOverflow = ValueOf<typeof TABLE_CELL_TEXT_OVERFLOW>

/**
 * Define as opções de controle de quebra de linha para células da tabela.
 *
 * @remarks Valores possíveis:
 * - nowrap
 * - normal
 */
export type TableCellWhiteSpace = ValueOf<typeof TABLE_CELL_WHITE_SPACE>

/**
 * Define as opções de controle de overflow para a tabela como um todo.
 *
 * @remarks Valores possíveis:
 * - hidden
 * - visible
 */
export type TableOverflow = ValueOf<typeof TABLE_OVERFLOW>

/**
 * Define os estados semânticos suportados para o tooltip das células.
 *
 * @remarks Valores possíveis:
 * - info
 * - warning
 * - danger
 * - success
 */
export type TableTooltipState = ValueOf<typeof TABLE_TOOLTIP_STATE>

/**
 * Define os modos de resolução do tooltip nas células.
 *
 * @remarks Valores possíveis:
 * - inherit
 * - enabled
 * - disabled
 */
export type TableTooltipMode = ValueOf<typeof TABLE_TOOLTIP_MODE>
